import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { contractorToken, portalContractor } from '../lib/contractorAuth';
import './WorkOrders.css';
import { fmtDate } from '../lib/when';

const WorkOrdersList = ({ isAdmin }) => {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterContractor, setFilterContractor] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  
  // Contractors for filter dropdown
  const [contractors, setContractors] = useState([]);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const ITEMS_PER_PAGE = 50;

  // Fetch contractors for filter
  useEffect(() => {
    if (isAdmin) {
      const fetchContractors = async () => {
        try {
          const response = await axios.get(
            `${API_BASE}/contractors`
          );
          setContractors(response.data || []);
        } catch (err) {
          console.error('Error fetching contractors:', err);
        }
      };
      fetchContractors();
    }
  }, [isAdmin, API_BASE]);

  // Fetch work orders with filters
  const fetchWorkOrders = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const offset = (page - 1) * ITEMS_PER_PAGE;
      
      // Build query params
      const params = {
        limit: ITEMS_PER_PAGE,
        offset: offset,
      };
      
      if (filterStatus) params.status = filterStatus;
      if (isAdmin && filterContractor) params.contractor_id = filterContractor;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      
      const endpoint = isAdmin ? '/work-orders' : '/my-work-orders';
      /*
       * AN ADMINISTRATOR LOOKING THROUGH SOMEBODY ELSE'S PORTAL names them.
       *
       * /my-work-orders resolves a portal token, and failing that serves an
       * administrator who passes contractor_id (routes/work-orders.js) - that
       * branch has been there all along and nothing called it. Without this
       * the page answered "this list belongs to a contractor" to the one
       * person entitled to see every list, which is why View as used to send
       * an administrator to the firm-wide page instead of showing them what
       * the contractor sees.
       */
      const viewed = portalContractor();
      if (!isAdmin && viewed?.viewing && viewed.id) params.contractor_id = viewed.id;
      // The contractor's own credential. lib/apiClient attaches only the staff
      // token, so /my-work-orders - which resolves a portal token and nothing
      // else - received no credential and answered 401 for the very person it
      // is named after. Undefined for staff, who keep the interceptor's token.
      const token = contractorToken();
      const response = await axios.get(
        `${API_BASE}${endpoint}`,
        {
          params,
          ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {})
        }
      );
      
      setWorkOrders(response.data.workOrders || []);
      setTotalCount(response.data.total || 0);
      setTotalPages(Math.ceil((response.data.total || 0) / ITEMS_PER_PAGE));
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching work orders:', err);
      setError('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterContractor, filterDateFrom, filterDateTo, isAdmin, API_BASE]);

  // Fetch work orders on filter change
  useEffect(() => {
    fetchWorkOrders(1);
  }, [filterStatus, filterContractor, filterDateFrom, filterDateTo, fetchWorkOrders]);

  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchWorkOrders(newPage);
    }
  };

  // Trailing zeros off, so 2h rather than 2.00h, matching the detail page.
  const hours = (n) => `${Number(n).toFixed(2).replace(/\.?0+$/, '')}h`;

  const getVarianceIndicator = (allocated, logged) => {
    // No allocation means there is no percentage to quote.
    //
    // This divided by zero and printed the result: WO-JAR-2026-0003 read
    // "✗ Infinity%" on the live list, because that job has no allocated
    // hours. Saying 0% would be worse than Infinity - it reads as "nothing
    // used" next to 13 hours that were - so it says how much was logged
    // against nothing, which is the actual situation.
    const alloc = Number(allocated) || 0;
    const used = Number(logged) || 0;
    if (alloc <= 0) {
      return (
        <span className="variance-indicator variance-none" title="No hours were allocated to this job">
          {used > 0 ? `${hours(used)} / no allocation` : 'No allocation'}
        </span>
      );
    }

    const percentUsed = (used / alloc) * 100;

    if (percentUsed <= 90) {
      return <span className="variance-indicator variance-ok">✓ {percentUsed.toFixed(0)}%</span>;
    } else if (percentUsed <= 100) {
      return <span className="variance-indicator variance-warning">⚠ {percentUsed.toFixed(0)}%</span>;
    } else {
      return <span className="variance-indicator variance-over">✗ {percentUsed.toFixed(0)}%</span>;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'draft':
        return 'status-draft';
      case 'issued':
        return 'status-issued';
      case 'in_progress':
        return 'status-in_progress';
      case 'completed':
        return 'status-completed';
      default:
        return 'status-draft';
    }
  };

  return (
    <div className="work-orders-page work-orders-container">
      <div className="work-orders-header">
        <h1 className="work-orders-title">
          {isAdmin ? 'All Work Orders' : 'My Work Orders'}
        </h1>
        <span style={{ fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          {totalCount} total
        </span>
      </div>

      {/* Filters */}
      <div className="work-orders-filters">
        <div className="filter-group">
          <label>Status</label>
          <select value={filterStatus} onChange={handleFilterChange(setFilterStatus)}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>

        {isAdmin && (
          <div className="filter-group">
            <label>Contractor</label>
            <select value={filterContractor} onChange={handleFilterChange(setFilterContractor)}>
              <option value="">All Contractors</option>
              {contractors.map(contractor => (
                <option key={contractor.id} value={contractor.id}>
                  {contractor.name}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="filter-group">
          <label>From Date</label>
          <input 
            type="date" 
            value={filterDateFrom}
            onChange={handleFilterChange(setFilterDateFrom)}
          />
        </div>

        <div className="filter-group">
          <label>To Date</label>
          <input 
            type="date" 
            value={filterDateTo}
            onChange={handleFilterChange(setFilterDateTo)}
          />
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="work-orders-error">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="work-orders-loading">
          Loading work orders...
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && workOrders.length === 0 && (
        <div className="work-orders-empty">
          No work orders found
        </div>
      )}

      {/* Table */}
      {!loading && !error && workOrders.length > 0 && (
        <>
          <div className="work-orders-table-wrapper">
            <table className="work-orders-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Description</th>
                  {isAdmin && <th>Contractor</th>}
                  <th>Status</th>
                  <th>Allocated</th>
                  <th>Logged</th>
                  <th>Usage</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {workOrders.map(workOrder => (
                  <tr key={workOrder.id}>
                    <td>
                      <Link 
                        to={`/work-orders/${workOrder.id}`}
                        className="work-order-id"
                      >
                        {workOrder.work_order_number || `WO-${workOrder.id}`}
                      </Link>
                    </td>
                    <td>
                      <div style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {workOrder.description}
                      </div>
                    </td>
                    {isAdmin && <td>{workOrder.contractor_name || '-'}</td>}
                    <td>
                      <span className={`status-badge ${getStatusColor(workOrder.status)}`}>
                        {(workOrder.status || 'unknown').replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td>{workOrder.allocated_hours}h</td>
                    <td>{workOrder.logged_hours || 0}h</td>
                    <td>{getVarianceIndicator(workOrder.allocated_hours, workOrder.logged_hours || 0)}</td>
                    <td style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                      {fmtDate(workOrder.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="pagination-item"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                ← Previous
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`pagination-item ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              ))}
              
              <button 
                className="pagination-item"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WorkOrdersList;
